import React, { useEffect, useState } from 'react';
import { Grid, Paper, Typography, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchSurveyChart, MenuProps, exportSurveyChart } from '../../Widgets/WidgetService';
import SurveyChartSummary from '../../../Models/Widget/SurveyChartSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { parentUrl } from '../../../utils/constants';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { dateFormatter } from '../../../utils/timeFormat';
import { generateRandomId } from '../../../utils/common';
import '../Widgets.css';

const SurveySummaryDetail = () => {
    const history = useNavigate();
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const {
        surveySelectedProvider,
        setSurveySelectedProvider,
        surveySelectedFilter,
        setSurveySelectedFilter,
        surveyFromDate,
        setSurveyFromDate,
        surveyToDate,
        setSurveyToDate,
        surveyShowInActiveProviders,
        setSurveyShowInActiveProviders,
    } = useWidgetContext();
    const [members, setMembers] = useState<SurveyChartSummary>();
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const filteredProviders = (surveyShowInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];

    const dateFilterList = [
        { Id: 1, Value: 'Last 3 Months' },
        { Id: 2, Value: 'Last 6 Months' },
        { Id: 3, Value: 'Last 1 Year' },
        { Id: 4, Value: 'Custom' },
    ];

    useEffect(() => {
        if (surveySelectedFilter !== TimePeriodFilterEnum.Custom || (Boolean(surveyFromDate) && Boolean(surveyToDate))) {
            const changeFilter = async () => {
                setIsMembersLoading(false);
                let start = surveyFromDate !== null ? new Date(surveyFromDate).toDateString() : null;
                let end = surveyToDate !== null ? new Date(surveyToDate).toDateString() : null;

                const data = await fetchSurveyChart(surveySelectedProvider, surveySelectedFilter, start, end, true);
                setMembers(data);
                setIsMembersLoading(true);
            };

            changeFilter();
        }

        if (surveySelectedFilter !== TimePeriodFilterEnum.Custom && Boolean(surveyFromDate) && Boolean(surveyToDate)) {
            setSurveyFromDate(null);
            setSurveyToDate(null);
        }
    }, [surveySelectedProvider, surveySelectedFilter, surveyFromDate, surveyToDate, surveyShowInActiveProviders, setSurveyFromDate, setSurveyToDate]);

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const changeFromDate = (date: any) => {
        setSurveyFromDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(surveyToDate) || new Date(surveyToDate) <= dateInfo) {
            let endDate = dateInfo.setDate(dateInfo.getDate() + 30);
            setSurveyToDate(endDate);
        }
    };

    const changeToDate = (date: any) => {
        setSurveyToDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(surveyFromDate) || new Date(surveyFromDate) >= dateInfo) {
            let startDate = dateInfo.setDate(dateInfo.getDate() - 30);
            setSurveyFromDate(startDate);
        }
    };

    const exportDetail = async () => {
        setDownLoading(true);
        let start = surveyFromDate !== null ? new Date(surveyFromDate).toDateString() : null;
        let end = surveyToDate !== null ? new Date(surveyToDate).toDateString() : null;
        const data = await exportSurveyChart(surveySelectedProvider, surveySelectedFilter, start, end);
        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `SurveySummary - ${dateInfo}.xlsx`);
        link.click();
        link.remove();

        setDownLoading(false);
    };

    const toggleInactiveHandler = (isChecked: boolean) => {
        setSurveyShowInActiveProviders(isChecked);
        setSurveySelectedProvider('All');
    };

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return <Typography>{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>;
            },
        },
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateInfo',
            headerName: 'Appointment Date',
            width: 150,
        },
    ];

    const columnList: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return <Typography>{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>;
            },
        },
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateInfo',
            headerName: 'Appointment Date',
            width: 150,
            valueGetter: (value, row) => `${Boolean(row.DateInfo) ? dateFormatter(new Date(row.DateInfo)) : '-'}`,
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Survey Summary
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={0} spacing={2}>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Providers</InputLabel>
                            <Select
                                value={surveySelectedProvider}
                                label="Providers"
                                name="Providers"
                                onChange={(e) => setSurveySelectedProvider(e.target.value)}
                                MenuProps={MenuProps}
                            >
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                <FormControlLabel
                                    sx={{ pl: '12px', mb: '0px' }}
                                    control={<Checkbox checked={surveyShowInActiveProviders} onChange={(e) => toggleInactiveHandler(e.target.checked)} />}
                                    label="Show Inactive"
                                />
                                {filteredProviders.map((pro) => (
                                    <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                        {pro.LastName}, {pro.FirstName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Date Filter</InputLabel>
                            <Select
                                value={surveySelectedFilter}
                                label="Date Filter"
                                name="Date"
                                onChange={(e) => setSurveySelectedFilter(parseInt(e.target.value as any))}
                            >
                                {dateFilterList.map((date) => (
                                    <MenuItem key={date.Id} value={date.Id}>
                                        {date.Value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {surveySelectedFilter === TimePeriodFilterEnum.Custom ? (
                        <React.Fragment>
                            <Grid size={{ md: 1.5 }}>
                                <DesktopDatePicker
                                    label="From Date"
                                    value={Boolean(surveyFromDate) ? surveyFromDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => changeFromDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                            <Grid size={{ md: 1.5 }}>
                                <DesktopDatePicker
                                    label="To Date"
                                    value={Boolean(surveyToDate) ? surveyToDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => changeToDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                        </React.Fragment>
                    ) : null}
                    <Grid size={parseInt(surveySelectedFilter) === TimePeriodFilterEnum.Custom ? 4.8 : 7.8}></Grid>
                    <Grid size={1}>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportDetail()}
                            variant="contained"
                            color="success"
                            disabled={members?.SurveyChartData?.length === 0}
                        >
                            {downLoading ? 'Exporting..' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} minHeight="60vh" sx={{ position: 'relative' }}>
                        {!isMembersLoading ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container columnSpacing={3}>
                                {members.SurveyChartData.map((detail, index) => (
                                    <>
                                        <Grid size={12} textAlign="center" paddingTop={index === 0 ? 0 : 2}>
                                            <Typography display={'inline-block'} variant="h5" color={'default'} marginTop={1}>
                                                {detail.Date}
                                            </Typography>
                                            <hr></hr>
                                        </Grid>
                                        <Grid size={6} textAlign="center">
                                            <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                                Appointments ({detail.Appointments.length})
                                            </Typography>
                                            <Grid size={12} style={{ height: '67vh', width: '100%' }}>
                                                <DataGrid
                                                    columns={columns}
                                                    rows={detail.Appointments}
                                                    getRowId={(row) => `${row.ItemId}-${row.DateInfo}-${generateRandomId()}`}
                                                    paginationMode="server"
                                                    hideFooterPagination={true}
                                                    hideFooter={true}
                                                    onCellClick={(params: any) => {
                                                        window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid size={6} textAlign="center">
                                            <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                                Surveys ({detail.Surveys.length})
                                            </Typography>
                                            <Grid size={12} style={{ height: '67vh', width: '100%' }}>
                                                <DataGrid
                                                    columns={columnList}
                                                    rows={detail.Surveys}
                                                    getRowId={(row) => `${row.ItemId}-${generateRandomId()}`}
                                                    paginationMode="server"
                                                    hideFooterPagination={true}
                                                    hideFooter={true}
                                                    onCellClick={(params: any) => {
                                                        window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                ))}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default SurveySummaryDetail;
